/**
 * Create RMA request store.
 *
 * Holds one draft's Create RMA model and derived UI state for the dashboard Create RMA flow.
 * The fee configuration, the automations and the internal note are not per draft: they live on the
 * shared CreateRmaSessionStore (FR7a), which this store reads when it builds its request model.
 */

import {IReactionDisposer, autorun, makeAutoObservable} from "mobx";
import {AMOUNT_TYPE, FEE_TYPE} from "../../policies/policy-types";
import {RETURN_METHOD_TYPE} from "../../return-methods/return-method-types";
import {RESOLUTION_TYPE} from "../../resolutions/constants";
import type CreateRmaSessionStore from "./create-rma-session-store";
import type {FeeKey} from "./create-rma-session-store";
import CreateRmaSettingsStore from "./create-rma-settings-store";
import OrderProductStore from "./order-product-store";
import OrderStore from "./order-store";
import RequestProductStore from "./request-product-store";
import requestProductStore, {ICreateRmaItemDto} from "./request-product-store";
import {IAutoRefundDto} from "./rma-auto-refund-store";
import RmaFeeStore from "./rma-fee-store";
import RmaFeesListStore from "./rma-fees-list-store";
import CreateRmaPolicyFeeDerivationStore from "./create-rma-policy-fee-derivation-store";
import { CreateRmaEligibilityStore } from "./create-rma-eligibility-store";
import { CreateRmaBalancePreviewStore } from "./create-rma-balance-preview-store";
import {IManagerCredentials} from "../../../manager-approval-modal/manager-credentials-stores";
import { ICRMAFeeDto } from "../../../models/User";
import CreateRmaDynamicShippingOptionsStore from "./create-rma-dynamic-shipping-store";
import { CREATE_RMA_MODE } from "../../../services/api/create-rma-service";

interface IRmaRequestChanges extends Partial<ICreateRmaDto> {
	payment_currency_code: string;
	selected_dynamic_shipping_option_id?: number | null;
}
export interface ICreateRmaDto {
	order_id: string;
	order_name: string;
    locale?: string;
	items: ICreateRmaItemDto[];
	return_fee: ICRMAFeeDto;
	label_fee: ICRMAFeeDto;
	auto_refund: IAutoRefundDto;
	auto_mark_as_done: boolean;
	is_auto_approved: boolean;
	send_customer_emails: boolean;
    payment_currency_code: string
	note?: string;
    manager_approval_credentials?: IManagerCredentials;
	/** When dynamic shipping is required, the option chosen in the pre-submit modal (server field name). */
	selected_dynamic_shipping_option_id?: number | null;
}


class RmaRequestStore {
	private _requestProductStoreList: RequestProductStore[] = [];
	private _model: IRmaRequestChanges;
	private _ownFeeDerivation: CreateRmaPolicyFeeDerivationStore;
    private _eligibilityStore: CreateRmaEligibilityStore;
	private _balancePreviewStore: CreateRmaBalancePreviewStore;
    private _dynamicShippingOptionsStore: CreateRmaDynamicShippingOptionsStore;
    private _autorunDispose: IReactionDisposer;

	constructor(
        private _orderStore: OrderStore,
        private _settingsStore: CreateRmaSettingsStore,
        private _session: CreateRmaSessionStore,
    ) {
		makeAutoObservable(this, {}, {autoBind: true});

        this._model = {
            payment_currency_code: this._orderStore.paymentCurrencyCode,
			selected_dynamic_shipping_option_id: undefined,
		};

        this._session.register(this);
        this._ownFeeDerivation = new CreateRmaPolicyFeeDerivationStore(this._settingsStore);
        this._dynamicShippingOptionsStore = new CreateRmaDynamicShippingOptionsStore(this);
        this._eligibilityStore = new CreateRmaEligibilityStore(this);
		this._balancePreviewStore = new CreateRmaBalancePreviewStore(this);
        /** subscribing to changes in store to make requests for eligibility checks,
         * needed for is out of policy functionality to update eligibility state every time something in request changes
         * AND needed to fetch dynamic shipping options every time something in request changes */
        this._autorunDispose = autorun(() => {
            if(this.eligibilityStore?.shouldCheck()) {
                this.eligibilityStore?.checkEligibility();
                /** fetch dynamic shipping options every time something in request changes, shouldCheck() checks if the model was changed */
                this.dynamicShippingOptionsStore.fetchDynamicShippingOptions();
            }
        });
	}

    /** cleanup when class is disposed */
    public __destroy(): void {
        this._autorunDispose();
		this._balancePreviewStore.__destroy();
        this._session.unregister(this);
    }

    get paymentCurrencyCode(): string { return this._model.payment_currency_code ?? ''; }
	get requestProducts():RequestProductStore[] { return this._requestProductStoreList;}
	/**
	 * The effective fee configuration for this draft (FR7d): the shared session store in Unlimited
	 * mode, and this draft's own derivation in policy-led mode.
	 */
	get rmaFeesListStore(): RmaFeesListStore {
		return this._settingsStore.isPolicyLedMode
			? this._ownFeeDerivation.feesListStore
			: this._session.feesListStore;
	}
	get total(): number{ return this.getFeeSummary('returnFee') + this.getFeeSummary('labelFee') + this.itemsSubtotal + this.restockingFeeTotal + this.itemsAdjustments; }
	get itemsSubtotal(): number {
		let subtotal = 0;
		this._requestProductStoreList.forEach((RPS: requestProductStore)=>{
			subtotal += RPS.paidAmountSummary;
		})
		return subtotal;
	}
	get itemsAdjustments(): number {
		let subtotal = 0;
		this._requestProductStoreList.forEach((RPS: requestProductStore)=>{
			subtotal += RPS.adjustmentsSummary;
		})
		return subtotal;
	}
    get restockingFeeTotal() {
        return this.requestProducts.reduce((acc, product)=> acc += product.restockingFeeSummary ,0);
    }
	get autoRefundStore(){ return this._session.autoRefundStore; }
	get getRequestInEditMode(): RequestProductStore | undefined { return this.requestProducts.find(RPS => RPS.editMode); }
    get eligibilityStore(): CreateRmaEligibilityStore { return this._eligibilityStore; }
	get balancePreviewStore(): CreateRmaBalancePreviewStore { return this._balancePreviewStore; }
    get dynamicShippingOptionsStore(): CreateRmaDynamicShippingOptionsStore { return this._dynamicShippingOptionsStore; }

    getOrderProductAvailableQuantity(orderProductStore: OrderProductStore): number{
		let quantity = orderProductStore.quantity;
		let rmaQuantity = 0;
		// Count all products added to RMA (can be added multiple times)
		this.requestProducts.forEach(
			(RPS) => {
				rmaQuantity += (RPS.product === orderProductStore ? RPS.quantity! : 0);
			}
		);

		return (quantity - rmaQuantity);
	}

	getOrderProductTotalPaidAmount(orderProductStore: OrderProductStore): number{
		let availableQuantity = this.getOrderProductAvailableQuantity(orderProductStore);
		return this.getOrderProductPaidAmount(orderProductStore) * availableQuantity;
	}

	/** Unit paid amount for the current Create RMA mode, as the server will count it. */
	getOrderProductPaidAmount(orderProductStore: OrderProductStore): number {
		return orderProductStore.getPaidAmount(this._settingsStore.cRmaMode === CREATE_RMA_MODE.C_RMA_GOD_MODE);
	}

    addProductStore(productStore: RequestProductStore) {
        const sameItem = this._requestProductStoreList.find(item => item.isSame(productStore));
        if(sameItem){
            sameItem.setQuantity(sameItem.quantity! + productStore.quantity!);
        }
        else {
    		this._requestProductStoreList.push(productStore);
        }
        this._session.recalculateFees();
	}

	saveProductStore(store: RequestProductStore){
		store.setEditMode(false);
		this._requestProductStoreList = this._requestProductStoreList.map(RPS => RPS.editMode ? store : RPS);
        this._session.recalculateFees();
	}

	removeProduct(product: RequestProductStore){
		this._requestProductStoreList.splice(this._requestProductStoreList.indexOf(product), 1);
        this._session.recalculateFees();
	}

	getProductRequestToEdit(product: OrderProductStore, model?: ICreateRmaItemDto){
		return new RequestProductStore(product, this,this._settingsStore, model, this._orderStore);
	}

    getConflictingReturnMethodLabel(returnMethodId: number): string | undefined {
       /*
       * Logic is this:
       * No shipment required goes with anything
       * All other three (paid label, dropoff, ship with any carrier) goe with no shipment require or the exact same return method id.
       */
        const returnMethodType = this._settingsStore.findReturnMethod(returnMethodId)?.type;
        if (returnMethodType === RETURN_METHOD_TYPE.NO_SHIPMENT_REQUIRED) {
            return undefined;
        }
        else {
            return this.requestProducts.find(item => {
                if(item.editMode){
                    return false;
                }
                const itemReturnMethodType = this._settingsStore.findReturnMethod(item.returnMethodId!)?.type;
                //combination is allowed
                if(itemReturnMethodType === RETURN_METHOD_TYPE.NO_SHIPMENT_REQUIRED){
                    return false;
                }
                //else - if not same return method id combination not allowed
                if(returnMethodId !== item.returnMethodId){
                    return true;
                }
                return false;

            })?.returnMethodLabel;
        }
    }

    private _getFeeCalculation(feeStore: RmaFeeStore, cost: number) {
        /** TODO: this block of calculation should probably be moved to server */
        const totalQuantity = () => { return this.requestProducts.reduce((acc, p) => acc + (p.quantity ?? 0), 0); }
        const totalPaidAmount = () => { return this.requestProducts.reduce((acc, p) => acc + p.paidAmount * (p.quantity ?? 0), 0); }
        const totalCatalogPrice = () => { return this.requestProducts.reduce((acc, p) => acc + p.product.catalogPrice * (p.quantity ?? 0), 0); }

        const FEE_TYPE_MAP: Partial<Record<FEE_TYPE, () => number>> = {
            [FEE_TYPE.COST]: () => cost,
            [FEE_TYPE.COST_PLUS_ITEM_FEE]: () => feeStore.amount * totalQuantity() + cost,
            [FEE_TYPE.COST_PLUS_SHIPMENT_FEE]: () => feeStore.amount + cost,
        };

        const AMOUNT_TYPE_MAP: Record<AMOUNT_TYPE, () => number> = {
            [AMOUNT_TYPE.FIXED]: () => feeStore.amount,
            [AMOUNT_TYPE.ITEM_PERCENTAGE_PAID_PRICE]: () => (feeStore.amount / 100) * totalPaidAmount(),
            [AMOUNT_TYPE.ITEM_PERCENTAGE_CATALOG_PRICE]: () => (feeStore.amount / 100) * totalCatalogPrice(),
        };

        const fn = FEE_TYPE_MAP[feeStore.feeType!] ?? AMOUNT_TYPE_MAP[feeStore.amountType] ?? (() => 0);

        return fn();
    }


    /**
     * The fee amount this draft contributes, derived from the effective fee configuration applied to
     * this draft's own items and its own chosen carrier cost.
     */
    getFeeSummary(feeType: FeeKey): number {
        const feeStore = this.rmaFeesListStore[feeType];
        if (!feeStore.isCharged) {
            return 0;
        }
        return this._getFeeCalculation(feeStore, this.dynamicShippingOptionsStore.selectedOption?.cost ?? 0) * -1;
    }

	requestModel(): ICreateRmaDto {
		const orderStore = this._orderStore;

        const items: ICreateRmaItemDto[] = [];
        /** server expects not grouped items (quantity 1) */
        this.requestProducts.forEach(requestProduct => {
            for (let i = 0; i < requestProduct.quantity; i++) {;
                items.push({ ...requestProduct.requestModel(), quantity: 1 });
            }
        });

        const session = this._session.requestModel();

		let rmaRequest: ICreateRmaDto;
		rmaRequest = {
			...this._model,
            ...session,
			order_id: orderStore?.orderId ?? '',
			order_name: orderStore?.orderName ?? '',
            locale: orderStore?.locale,
			items: items,
			return_fee: this.rmaFeesListStore.returnFee.requestModel(),
			label_fee: this.rmaFeesListStore.labelFee.requestModel(),
			auto_refund: this.autoRefundStore.requestModel(),
            note: session.note !== '' ? session.note : null,
            selected_dynamic_shipping_option_id: this.dynamicShippingOptionsStore.selectedOption?.id,
		} as ICreateRmaDto;

		return rmaRequest;
	}

    getConflictingResolutionLabel(resolutionId: number): string | undefined {
        /*
        * Refund to payment method - Can only be combined with Refund to payment method and refund to bank account
        * Refund to store credit - Can only be combined with Refund to store credit and refund to bank account
        * Refund to gift card - Can only be combined with Refund to gift card and refund to bank account
        * Refund to bank account - Can be combined with anything
        */
        const currentResolutionType = this._settingsStore.findResolution(resolutionId)?.resolution_type;
        if (currentResolutionType === RESOLUTION_TYPE.BankRefund) {
            return undefined;
        }
        else {
            return this.requestProducts.find(item => {
                if(item.editMode){
                    return false;
                }
                const resolutionType = this._settingsStore.findResolution(item.resolutionId)?.resolution_type;
                return resolutionType !== currentResolutionType && resolutionType !== RESOLUTION_TYPE.BankRefund;
            })?.resolutionLabel;
        }
    }

    public isDynamicShippingOptionsSupported(): boolean {
        return this.dynamicShippingOptionsStore.isDynamicShippingOptionsSupported;
    }

    public doesLabelFeeIncludeCost(): boolean {
        const feeType = this.rmaFeesListStore.labelFee.feeType;
        if (!feeType) {
            return false;
        }
        return [FEE_TYPE.COST_PLUS_SHIPMENT_FEE, FEE_TYPE.COST, FEE_TYPE.COST_PLUS_ITEM_FEE].includes(feeType);
    }

    /** true when any of this draft's items uses a return method that supports the carrier's cost */
    public isLabelCostSupported(): boolean {
        return this._requestProductStoreList.some(ps => ps.isLabelCostSupported());
    }

    /** re-runs session + draft fee derivation after an item's policy fee amounts finish loading */
    public recalculateFeesFromPolicyAmounts(): void {
        this._session.recalculateFees();
    }

    /** re-derives this draft's own fee configuration from its items; a no-op in Unlimited mode */
    public recalculateOwnFees(): void {
        if (!this._settingsStore.isPolicyLedMode) {
            return;
        }

        this._ownFeeDerivation.deriveFromItems(this.requestProducts, {
            isLabelCostSupported: this.isLabelCostSupported(),
            shouldWaveAllFees: this.requestProducts.some((item) => item.hasWaveAllFeesPolicyRule()),
        });
    }
}

export default RmaRequestStore;